import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

function Analytics() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState("free");
  const [events, setEvents] = useState([]);

  const [stats, setStats] = useState({
    profileViews: 0,
    pitchOpens: 0,
    claimViews: 0,
    evidenceOpens: 0,
  });

  const [bySource, setBySource] = useState({
    public: { profile: 0, pitch: 0, claim: 0, evidence: 0 },
    evaluator: { profile: 0, pitch: 0, claim: 0, evidence: 0 },
    unknown: { profile: 0, pitch: 0, claim: 0, evidence: 0 },
  });

  const isPaid = plan === "paid";

  useEffect(() => {
    if (!user) return;
    const loadPlan = async () => {
      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) setPlan(snap.data().plan || "free");
      } catch (err) {
        console.error("Failed to load plan", err);
      }
    };
    loadPlan();
  }, [user]);

  useEffect(() => {
    if (!user || !isPaid) {
      setLoading(false);
      return;
    }

    const loadAnalytics = async () => {
      try {
        setLoading(true);
        const q = query(
          collection(db, "analytics"),
          where("ownerId", "==", user.uid)
        );
        const snap = await getDocs(q);
        const data = snap.docs.map((d) => d.data());
        setEvents(data);

        let profileViews = 0;
        let pitchOpens = 0;
        let claimViews = 0;
        let evidenceOpens = 0;

        const sourceMap = {
          public: { profile: 0, pitch: 0, claim: 0, evidence: 0 },
          evaluator: { profile: 0, pitch: 0, claim: 0, evidence: 0 },
          unknown: { profile: 0, pitch: 0, claim: 0, evidence: 0 },
        };

        data.forEach((e) => {
          const source = e.source || "unknown";
          switch (e.eventType) {
            case "profile_view":
              profileViews++;
              sourceMap[source].profile++;
              break;
            case "pitch_open":
              pitchOpens++;
              sourceMap[source].pitch++;
              break;
            case "claim_view":
              claimViews++;
              sourceMap[source].claim++;
              break;
            case "evidence_open":
              evidenceOpens++;
              sourceMap[source].evidence++;
              break;
            default:
              break;
          }
        });

        setStats({ profileViews, pitchOpens, claimViews, evidenceOpens });
        setBySource(sourceMap);
      } catch (err) {
        console.error("Failed to load analytics", err);
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, [user, isPaid]);

  if (loading) return <p>Loading analytics…</p>;
  if (!isPaid) return null;

  const engagementChartData = [
    {
      name: "Profile Views",
      Public: bySource.public.profile,
      Evaluator: bySource.evaluator.profile,
    },
    {
      name: "Pitch Opens",
      Public: bySource.public.pitch,
      Evaluator: bySource.evaluator.pitch,
    },
    {
      name: "Claims Viewed",
      Public: bySource.public.claim,
      Evaluator: bySource.evaluator.claim,
    },
    {
      name: "Evidence Opened",
      Public: bySource.public.evidence,
      Evaluator: bySource.evaluator.evidence,
    },
  ];

  return (
    <div>
      {/* HEADER */}
      <div style={{ marginBottom: 28 }}>
        <h1
          style={{ marginBottom: 6 }}
          title="Analytics show how people engage with your startup profile over time."
        >
          Analytics Overview
        </h1>
        <p
          style={{ color: "#64748b", maxWidth: 720, lineHeight: 1.6 }}
          title="Use these insights to understand investor interest, not vanity metrics."
        >
          Track how investors and evaluators interact with your startup
          profile, pitch, and supporting evidence.
        </p>
      </div>

      {/* KPI CARDS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 18,
          marginBottom: 40,
        }}
      >
        <StatCard
          label="Profile Views"
          value={stats.profileViews}
          color="#6366f1"
          tooltip="How many times your startup profile was opened."
        />
        <StatCard
          label="Pitch Opens"
          value={stats.pitchOpens}
          color="#0ea5e9"
          tooltip="Number of times your pitch deck was opened."
        />
        <StatCard
          label="Claims Viewed"
          value={stats.claimViews}
          color="#f59e0b"
          tooltip="How often evaluators or viewers inspected your claims."
        />
        <StatCard
          label="Evidence Opened"
          value={stats.evidenceOpens}
          color="#16a34a"
          tooltip="Strongest signal: viewers opening proof documents."
        />
      </div>

      {/* CHART SECTION */}
      <div
        className="card"
        style={{ padding: 28, marginBottom: 40 }}
        title="This chart compares shallow vs deep engagement."
      >
        <h3
          style={{ marginBottom: 6 }}
          title="Who is engaging, and how deeply?"
        >
          Engagement Depth by Source
        </h3>
        <p
          style={{ fontSize: 13, color: "#64748b", marginBottom: 18 }}
          title="Evaluator engagement is usually more meaningful than public traffic."
        >
          Compare how public viewers and evaluators engage with your startup.
        </p>

        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={engagementChartData}>
              <XAxis
                dataKey="name"
                title="Engagement type"
              />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="Public"
                stackId="a"
                fill="#6366f1"
                radius={[6, 6, 0, 0]}
              />
              <Bar
                dataKey="Evaluator"
                stackId="a"
                fill="#16a34a"
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* RECENT ACTIVITY */}
      <div
        className="card"
        style={{ padding: 28 }}
        title="Recent engagement helps you detect momentum."
      >
        <h3
          style={{ marginBottom: 16 }}
          title="Short-term activity trend."
        >
          Recent Engagement (Last 7 Days)
        </h3>

        {(() => {
          const now = new Date();
          const sevenDaysAgo = new Date(
            now.getTime() - 7 * 24 * 60 * 60 * 1000
          );
          const recent = events.filter(
            (e) =>
              e.timestamp?.toDate &&
              e.timestamp.toDate() >= sevenDaysAgo
          );

          if (recent.length === 0) {
            return (
              <p style={{ color: "#64748b" }}>
                No activity in the last 7 days.
              </p>
            );
          }

          const summary = {
            profile: 0,
            pitch: 0,
            claim: 0,
            evidence: 0,
          };

          recent.forEach((e) => {
            switch (e.eventType) {
              case "profile_view":
                summary.profile++;
                break;
              case "pitch_open":
                summary.pitch++;
                break;
              case "claim_view":
                summary.claim++;
                break;
              case "evidence_open":
                summary.evidence++;
                break;
              default:
                break;
            }
          });

          return (
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(160px, 1fr))",
                gap: 18,
              }}
            >
              <MiniStat
                label="Profile views"
                value={summary.profile}
                tooltip="Initial interest signal."
              />
              <MiniStat
                label="Pitch opens"
                value={summary.pitch}
                tooltip="Shows intent to understand your story."
              />
              <MiniStat
                label="Claims viewed"
                value={summary.claim}
                tooltip="Viewers are scrutinizing your assertions."
              />
              <MiniStat
                label="Evidence opened"
                value={summary.evidence}
                tooltip="Strongest credibility signal."
              />
            </div>
          );
        })()}
      </div>
    </div>
  );
}

/* UI COMPONENTS */

const StatCard = ({ label, value, color, tooltip }) => (
  <div
    className="card"
    style={{ padding: 20, borderTop: `4px solid ${color}` }}
    title={tooltip}
  >
    <div style={{ fontSize: 13, color: "#64748b", marginBottom: 6 }}>
      {label}
    </div>
    <div style={{ fontSize: 30, fontWeight: 700 }}>{value}</div>
  </div>
);

const MiniStat = ({ label, value, tooltip }) => (
  <div
    style={{
      padding: 16,
      background: "#f8fafc",
      borderRadius: 10,
    }}
    title={tooltip}
  >
    <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
    <div style={{ fontSize: 13, color: "#64748b" }}>{label}</div>
  </div>
);

export default Analytics;
